import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from "react";
import { injectThemeCSS } from "../config/branding";
import {
  THEME_PRESETS,
  getPresetById,
  mergeCustomTheme,
  type ThemePreset,
} from "../config/theme-presets";

type Theme = "light" | "dark" | "crimson";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  activePresetId: string;
  customTheme: Partial<ThemePreset> | null;
  activeTheme: ThemePreset;
  isLoading: boolean;
  setPreset: (id: string) => void;
  setCustomTheme: (overrides: Partial<ThemePreset>) => void;
  resetCustomTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = "dashboard-theme";
const PRESET_STORAGE_KEY = "dashboard-preset";
const CUSTOM_THEME_STORAGE_KEY = "dashboard-custom-theme";
const DEFAULT_PRESET_ID = "infoscalers";
const LEGACY_PRESET_IDS = new Set(["modern"]);

function isEmbeddedDashboardUrl(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const url = new URL(window.location.href);
    return url.pathname.startsWith("/offer/") && url.searchParams.has("dashboard");
  } catch {
    return false;
  }
}

function loadFromStorage<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) return fallback;
    try {
      return JSON.parse(stored) as T;
    } catch {
      return stored as T;
    }
  } catch {
    return fallback;
  }
}

function hasStoredValue(key: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function normalizePresetId(presetId: string | null | undefined): string {
  if (!presetId) return DEFAULT_PRESET_ID;
  return LEGACY_PRESET_IDS.has(presetId) ? DEFAULT_PRESET_ID : presetId;
}

function saveToStorage(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage full or unavailable
  }
}

async function savePrefsToAPI(data: {
  presetId?: string;
  customTheme?: Partial<ThemePreset> | null;
  darkMode?: string;
}): Promise<void> {
  try {
    await fetch("/api/user/preferences", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  } catch {
    // API unavailable — localStorage fallback is already saved
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const storedTheme = loadFromStorage<Theme | null>(THEME_STORAGE_KEY, null);
    return storedTheme === "light" || storedTheme === "dark" || storedTheme === "crimson" ? storedTheme : "dark";
  });

  const [activePresetId, setActivePresetId] = useState<string>(() =>
    normalizePresetId(loadFromStorage(PRESET_STORAGE_KEY, DEFAULT_PRESET_ID))
  );

  const [customTheme, setCustomThemeState] = useState<Partial<ThemePreset> | null>(() =>
    loadFromStorage<Partial<ThemePreset> | null>(CUSTOM_THEME_STORAGE_KEY, null)
  );

  const [isLoading, setIsLoading] = useState(true);

  // Resolve the active theme by merging preset + custom overrides
  const activeTheme = useMemo((): ThemePreset => {
    const base = getPresetById(activePresetId) ?? THEME_PRESETS[0]!;
    if (customTheme && Object.keys(customTheme).length > 0) {
      return mergeCustomTheme(base, customTheme);
    }
    return base;
  }, [activePresetId, customTheme]);

  // Load preferences from API on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const storedTheme = loadFromStorage<Theme | null>(THEME_STORAGE_KEY, null);
        const hasStoredTheme = storedTheme === "light" || storedTheme === "dark";
        const storedPresetId = normalizePresetId(loadFromStorage<string | null>(PRESET_STORAGE_KEY, null));
        const hasStoredPreset = hasStoredValue(PRESET_STORAGE_KEY) && Boolean(getPresetById(storedPresetId));
        const storedCustomTheme = loadFromStorage<Partial<ThemePreset> | null>(CUSTOM_THEME_STORAGE_KEY, null);
        const hasStoredCustomTheme = hasStoredValue(CUSTOM_THEME_STORAGE_KEY);

        const res = await fetch("/api/user/preferences");
        if (!res.ok) throw new Error("API error");
        const json = await res.json() as {
          success: boolean;
          data: { presetId?: string; customTheme?: Partial<ThemePreset> | null; darkMode?: string };
        };
        if (cancelled || !json.success) return;
        const { data } = json;
        const apiPresetId = normalizePresetId(data.presetId);
        const resolvedPresetId = hasStoredPreset
          ? storedPresetId
          : (getPresetById(apiPresetId) ? apiPresetId : DEFAULT_PRESET_ID);
        const resolvedCustomTheme = hasStoredCustomTheme
          ? storedCustomTheme
          : (data.customTheme ?? null);
        const resolvedTheme = hasStoredTheme
          ? storedTheme
          : (data.darkMode === "light" || data.darkMode === "dark" || data.darkMode === "crimson" ? data.darkMode : "dark");

        setActivePresetId(resolvedPresetId);
        saveToStorage(PRESET_STORAGE_KEY, resolvedPresetId);

        setCustomThemeState(resolvedCustomTheme);
        saveToStorage(CUSTOM_THEME_STORAGE_KEY, resolvedCustomTheme);

        setThemeState(resolvedTheme);
        saveToStorage(THEME_STORAGE_KEY, resolvedTheme);

        if (hasStoredTheme || hasStoredPreset || hasStoredCustomTheme) {
          void savePrefsToAPI({
            presetId: resolvedPresetId,
            customTheme: resolvedCustomTheme,
            darkMode: resolvedTheme,
          });
        }
      } catch {
        // Fall back to localStorage values (already loaded in initial state)
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Apply theme class + inject CSS vars whenever theme/preset changes
  useEffect(() => {
    const embeddedDashboard = isEmbeddedDashboardUrl();
    const root = document.documentElement;
    saveToStorage(THEME_STORAGE_KEY, theme);

    if (embeddedDashboard) {
      return;
    }

    // Reset background image first
    document.body.style.backgroundImage = "";
    document.body.style.backgroundColor = "";
    root.classList.remove("dark", "dark-bg", "crimson");

    if (theme === "dark") {
      root.classList.add("dark");
      root.classList.add("dark-bg");
      document.body.style.backgroundImage = "url('/dark-bg.jpg')";
    } else if (theme === "crimson") {
      root.classList.add("crimson");
      document.body.style.backgroundImage = "url('/crimson-bg.jpg')";
    } else {
      const bgLight = activeTheme.colors.background.light;
      root.style.backgroundColor = bgLight;
      document.body.style.backgroundColor = bgLight;
    }

    // Use new theme injection if we have an active theme
    injectThemeCSS(activeTheme, theme === "dark" || theme === "crimson");
  }, [theme, activeTheme]);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === "light" ? "dark" : prev === "dark" ? "crimson" : "light";
      savePrefsToAPI({ darkMode: next });
      return next;
    });
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    savePrefsToAPI({ darkMode: newTheme as string });
  }, []);

  const setPreset = useCallback((id: string) => {
    setActivePresetId(id);
    setCustomThemeState(null);
    saveToStorage(PRESET_STORAGE_KEY, id);
    saveToStorage(CUSTOM_THEME_STORAGE_KEY, null);
    savePrefsToAPI({ presetId: id, customTheme: null });
  }, []);

  const setCustomTheme = useCallback((overrides: Partial<ThemePreset>) => {
    setCustomThemeState((prev) => {
      const merged = { ...(prev ?? {}), ...overrides };
      saveToStorage(CUSTOM_THEME_STORAGE_KEY, merged);
      savePrefsToAPI({ customTheme: merged });
      return merged;
    });
  }, []);

  const resetCustomTheme = useCallback(() => {
    setCustomThemeState(null);
    saveToStorage(CUSTOM_THEME_STORAGE_KEY, null);
    savePrefsToAPI({ customTheme: null });
  }, []);

  const value = useMemo<ThemeContextType>(
    () => ({
      theme,
      toggleTheme,
      setTheme,
      activePresetId,
      customTheme,
      activeTheme,
      isLoading,
      setPreset,
      setCustomTheme,
      resetCustomTheme,
    }),
    [theme, toggleTheme, setTheme, activePresetId, customTheme, activeTheme, isLoading, setPreset, setCustomTheme, resetCustomTheme]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

/** Returns the fully resolved active ThemePreset */
export function useActiveTheme(): ThemePreset {
  const { activeTheme } = useTheme();
  return activeTheme;
}
